import React, { useEffect, useState } from "react"
import { Box } from "@mui/material"
import { NodeSummary } from "./types"
import { FetchFromBackoffice } from "../../../utils/request"
import { useSearchParams } from "react-router-dom"
import { PageSection } from "../../../components/atoms/layouts"
import { SubpageTitle } from "../../../components/atoms/text/titles"
import { DateTime } from "luxon"
import ViewBox from "../../../components/atoms/views/viewBox"
import { DateRangePickerPopUp } from "../../../components/molecules/datepickers/dateRangePickerPopUp"
import { KPIDisplay } from "../salesTable"
import { useSelector } from "react-redux"
import { padding } from "../../../theme"
import TopListComponent, { TopListItem } from "../../dashboard/topListComponent"
import { CollapsibleTable, ColumnName } from "../../../components/molecules/CollapsableTable"

const RefreshData = (nodeId: string | null, from: DateTime, to: DateTime, update: (node: NodeSummary | undefined) => void) => {
  const year = from.year
  const month = from.month
  FetchFromBackoffice<NodeSummary>(`customer/kpis?year=${year}&month=${month}`)
    .then(node => {
      if (nodeId) {
        update(node.children.find(child => nodeId === child.id))
      } else {
        update({ ...node, name: "All" })
      }
    })
}

export const KpiReportsPage = () => {
  const today = DateTime.now()
  const [from, setFrom] = useState<DateTime>(today.startOf("month"))
  const [to, setTo] = useState<DateTime>(today.endOf("month"))
  const [node, setNode] = useState<NodeSummary | undefined>()
  const [search] = useSearchParams()
  const storeId = search.get('storeId')
  const [open, setOpen] = useState<boolean>(false)

  const isMobile = useSelector(
    (state: any) => state.dimensions.device === "mobile"
  );

  const prepareTopStores = (): TopListItem[] => {
    if (!node?.topStores) return [];

    return node.topStores.map((store) => ({
      name: store.name,
      countNumber: store.treatmentsPeriod,
      additionalCountNumber: Number(store.treatmentsPerDay.toFixed(1))
    }));
  };


  const prepareLowStores = (): TopListItem[] => {
    if (!node?.lowStores) return [];

    return node.lowStores.map((lowStore) => ({
      name: lowStore.name,
      countNumber: lowStore.treatmentsPeriod,
      additionalCountNumber: Number(lowStore.treatmentsPerDay.toFixed(1))

    }));
  };

  const allStoreItems = [...prepareLowStores(), ...prepareTopStores()]
  const max = Math.max(...allStoreItems.map(item => item.countNumber))

  useEffect(() => {
    RefreshData(storeId, from, to, setNode)
  }, [from, to, storeId])

  const totalRow = {
    store: "All",
    today: node?.treatmentsPerDay?.toFixed(0),
    total: node?.treatmentsPeriod?.toLocaleString("sv-SE"),
  };
  const columnNames: ColumnName[] = [
    {
      displayName: "Customer",
      id: "customer",
      grouped: true,
      aggregate: false,
      level: 0,
      align: "left",
      visible: false
    },
    {
      displayName: "Name",
      id: "store",
      grouped: false,
      aggregate: false,
      level: 1,
      align: "left",
      visible: true
    },
    {
      displayName: "Today",
      id: "today",
      grouped: false,
      level: 1,
      aggregate: true,
      align: "right",
      visible: true,
      formatter: (val) => Number(val).toLocaleString('sv-SE', {
        maximumFractionDigits: 0,
      }),
      info: "Treatments today"
    },
    {
      displayName: "Total",
      id: "total",
      grouped: false,
      level: 1,
      aggregate: true,
      align: "right",
      visible: true,
      formatter: (val) => Number(val).toLocaleString('sv-SE'),
      info: "Total treatments"
    },

  ]

  const prepData = node?.children.flatMap((customer) =>
    customer.children.map((store) => ({
      customer: customer.name,
      store: store.name,
      total: store.treatmentsPeriod,
      today: store.treatmentsPerDay,
    }))
  ).sort((a, b) => b.total - a.total);

  return (
    <Box
      hideBreadcrumbs={false}
      title={node?.name}
    >

      {/* <RoleContainer showForRoles={["Developer"]}>
        <div style={{ display: "flex", flexDirection: "row-reverse" }}>
          {storeId ? null : (<ViewFilterComponent viewName="report_sales_kpis_view" refresh={() => RefreshData(storeId, year, month, setNode)} />)}
        </div>
      </RoleContainer> */}
      <SubpageTitle title={"KPI"} />
      <Box>

        <PageSection>
          <div style={!isMobile ? { display: "flex", flexDirection: "row", marginBottom: 10 } : {}}>
            <ViewBox>
              <DateRangePickerPopUp
                startDate={from}
                endDate={to}
                onStartDatePicked={setFrom}
                onEndDatePicked={setTo}
                open={false}
                setOpen={setOpen}
                customDateRangePicker
              />
            </ViewBox>
            <div
              style={{
                marginBottom: padding * 8,
                marginLeft: padding * 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            ></div>
            <KPIDisplay
              description="Total"
              value={node?.treatmentsTotal !== undefined ? node?.treatmentsTotal.toLocaleString('sv-SE') : ""}
            />
            <KPIDisplay
              description="This month"
              value={node?.treatmentsPeriod !== undefined ? node?.treatmentsPeriod.toLocaleString('sv-SE') : ""}
              subText={
                node?.treatmentsPerDay !== undefined
                  ? `${node.treatmentsPerDay.toFixed(0)}/day`
                  : ""
              }
            />
            <KPIDisplay
              description="Stores"
              value={node?.nodesTotal !== undefined ? node?.nodesTotal : ""}
              updates={
                node?.nodesPeriod !== undefined
                  ? `${node.nodesPeriod} new`
                  : ""
              }
            />
            <KPIDisplay
              description="Hubs"
              updates={
                node?.machinesPeriod !== undefined
                  ? `${node.machinesPeriod} new`
                  : ""
              }

              value={node?.machinesTotal !== undefined ? node?.machinesTotal : ""} />
          </div>
        </PageSection>
        <PageSection>
          <SubpageTitle title={"Sales per store"} />
          <Box sx={!isMobile ? { display: "flex", flexDirection: "row", marginTop: 6, marginBottom: 6, gap: 4 } : {}}>
            <TopListComponent
              items={prepareTopStores()}
              title={"Top stores"}
              starIcon

            />
            <TopListComponent
              items={prepareLowStores()}
              title={"Low performing"}
              ascending={true}
              max={max}
            />
          </Box>
        </PageSection>
        <PageSection>
          <SubpageTitle title={"Total sales"} />
          <Box sx={[isMobile && { marginBottom: padding / 2 }]}>
            <CollapsibleTable
              columnNames={columnNames}
              rows={prepData ?? []}
              totalRow={totalRow}
            ></CollapsibleTable>
          </Box>
        </PageSection>
      </Box>
    </Box >
  )
}


